import { LinkedColumnType } from '@/features/canvas/types/linkage';

export type ValidationResult = {
  isValid: boolean;
  errorMessage?: string;
};

const validateDate = (value: string): ValidationResult => {
  if (!value) {
    return { isValid: true }; // Date can be empty
  }

  // Try to parse various date formats
  const datePatterns = [
    /^\d{1,2}\/\d{1,2}\/\d{4}$/, // DD/MM/YYYY or MM/DD/YYYY
    /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
    /^\d{1,2}-\d{1,2}-\d{4}$/, // DD-MM-YYYY
    /^\d{1,2}\.\d{1,2}\.\d{4}$/, // DD.MM.YYYY
  ];

  const hasValidFormat = datePatterns.some(pattern => pattern.test(value));

  if (!hasValidFormat) {
    return {
      isValid: false,
      errorMessage: 'Invalid date format. Use DD/MM/YYYY',
    };
  }

  // Basic validation to check day/month ranges (optional)
  // For DD/MM/YYYY format
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) {
    const [day, month] = value.split('/').map(Number);
    if (month < 1 || month > 12) {
      return { isValid: false, errorMessage: 'Invalid month' };
    }
    if (day < 1 || day > 31) {
      return { isValid: false, errorMessage: 'Invalid day' };
    }
  }

  return { isValid: true };
};

const validateBudget = (value: string): ValidationResult => {
  if (!value) {
    return { isValid: true }; // Budget fields can be empty
  }

  // Remove currency symbols and commas
  const cleanedValue = value.replace(/[£$€,]/g, '');

  // Check if it's a valid number
  if (!/^-?\d+(\.\d+)?$/.test(cleanedValue)) {
    return {
      isValid: false,
      errorMessage: 'Must be a valid number or currency amount',
    };
  }

  const numValue = parseFloat(cleanedValue);
  if (Number.isNaN(numValue)) {
    return {
      isValid: false,
      errorMessage: 'Must be a valid number',
    };
  }

  return { isValid: true };
};

const validateProgress = (value: string): ValidationResult => {
  if (!value) {
    return { isValid: true }; // Progress can be empty
  }

  // Remove percentage symbol if present
  const progressValue = value.replace('%', '').trim();

  // Check if it's a valid number
  if (!/^\d+(\.\d+)?$/.test(progressValue)) {
    return {
      isValid: false,
      errorMessage: 'Must be a number between 0 and 100',
    };
  }

  const numProgress = parseFloat(progressValue);
  if (Number.isNaN(numProgress) || numProgress < 0 || numProgress > 100) {
    return {
      isValid: false,
      errorMessage: 'Must be between 0 and 100',
    };
  }

  return { isValid: true };
};

/**
 * Validates a CSV cell value based on the field type it's linked to
 */
export const validateCell = (
  value: string | undefined | null,
  fieldType: keyof typeof LinkedColumnType
): ValidationResult => {
  // Convert value to string and trim
  const trimmedValue = String(value || '').trim();

  switch (fieldType) {
    case 'title':
      // Title is required and cannot be empty
      if (!trimmedValue) {
        return {
          isValid: false,
          errorMessage: 'Title is required',
        };
      }
      return { isValid: true };

    case 'date':
      return validateDate(trimmedValue);

    case 'budgetTotal':
    case 'budgetCapEx':
    case 'budgetOpEx':
    case 'budgetSpent':
      return validateBudget(trimmedValue);

    case 'progressBar':
      return validateProgress(trimmedValue);

    case 'description':
    case 'big':
    case 'small':
    case 'budget': // Custom budget fields can contain text
      // These fields can contain any text and can be empty
      return { isValid: true };

    case 'filter':
    case 'defaultFilter':
      // Filter fields can contain any value
      return { isValid: true };

    case 'dependencies':
      // Dependencies field contains comma-separated initiative titles
      // Can be empty or contain valid titles
      return { isValid: true };

    case 'children':
      // Children field contains comma-separated initiative titles
      // Can be empty or contain valid titles
      return { isValid: true };

    case 'createNewField':
    case 'createNewBudgetField':
    case 'createNewFilter':
      // These are special types for creating new fields, not for validation
      return { isValid: true };

    default:
      // For any unknown field types, consider them valid
      return { isValid: true };
  }
};

/**
 * Validates all cells in a column
 */
export const validateColumn = (
  columnValues: (string | undefined | null)[],
  fieldType: keyof typeof LinkedColumnType
): {
  hasErrors: boolean;
  validationResults: ValidationResult[];
} => {
  const validationResults = columnValues.map(value => validateCell(value, fieldType));
  const hasErrors = validationResults.some(result => !result.isValid);

  return {
    hasErrors,
    validationResults,
  };
};

/**
 * Checks if there are any validation errors in the linked columns
 */
export const hasValidationErrors = (
  dataRows: { cells: { value: string }[]; id: string }[],
  linkedColumns: {
    importedColumnUuid: string;
    linkedTargetType: keyof typeof LinkedColumnType;
  }[],
  columns: { id: string }[],
  editedValues?: Record<string, Record<string, string>>
): boolean => {
  return linkedColumns.some(linkedColumn => {
    const columnIndex = columns.findIndex(col => col.id === linkedColumn.importedColumnUuid);
    if (columnIndex === -1) return false;

    // Get values considering edited values
    const columnValues = dataRows.map(row => {
      const originalValue = row.cells[columnIndex]?.value || '';
      // Check if this cell has been edited
      const editedValue = editedValues?.[row.id]?.[linkedColumn.importedColumnUuid];
      return editedValue === undefined ? originalValue : editedValue;
    });

    const { hasErrors } = validateColumn(columnValues, linkedColumn.linkedTargetType);

    return hasErrors;
  });
};
